import type { EventSearchQuery, FeaturedEventQuery } from '../../types/peatix-api';

// Common event type definition
export type Event = {
  id: string;        // Event identifier
  date: string;      // Date
  endDate?: string;  // Optional end date (for multi-day events)
  title: string;     // Title
  venue?: string;    // Venue name (optional)
  venueAddress?: {   // Venue address (optional)
    streetAddress?: string;
    addressLocality?: string;
    postalCode?: string;
    addressRegion?: string;
    addressCountry?: string;
  };
  isFree?: boolean;  // Free event flag (optional)
  url: string;       // Event URL
  image?: string;    // Event image (optional)
};

// Guide section type definition
export type GuidePoint = {
  icon: string;
  title: string;
  /** Optional body text below the title. */
  description?: string;
};

/** Single related guide: link, label, and optional image for the card. */
export type RelatedGuide = {
  href: string;
  label: string;
  /** e.g. /host/images/discover-images/related-tokyo-culture.webp. Omit to use placeholder. */
  image?: string;
};

/** Config for FeaturedEventsSection. Each LP can define its own title, subtitle, fetch mode, and query. */
export type FeaturedSectionConfig = {
  title: string;
  subtitle: string;
  fetchMode?: 'featured' | 'search';
  itemsPerPage?: number;
  featuredQuery?: Partial<FeaturedEventQuery>;
  searchQuery?: Partial<EventSearchQuery>;
  loadingText?: string;
  emptyText?: string;
};

// Page content type definition
export type PageContent = {
  meta: {
    title: string;       // Title for the page
    description: string; // Page description
    image?: string;      // OGP image URL (falls back to default Peatix image)
  };
  hero: {
    description: string;
    title?: {
      highlight: string;
    };
    city?: string;
    heroImage: string;  // Hero image for the discover header
    /** Optional overlay logo rendered on top of the hero image (e.g. Visiting Japan badge). */
    overlayLogo?: string;
    /** When set, used as the hero title instead of "Upcoming {highlight} in {city}". */
    headline?: string;
    /** When set with headline, this substring is rendered with the gradient (purple-to-pink) to match the default highlight styling. */
    headlineHighlight?: string;
    /** Optional background image for the hero section. */
    backgroundImage?: string;
    /** Hero variant: 'centeredImage' renders just the image centered, default renders the two-column layout. */
    variant?: 'centeredImage';
  };
  statisticsAudience: string;
  highlightsImage?: string;
  guide: {
    points: GuidePoint[];
    guideImage: string;
    closingText: string;
    /** When set, used as the intro line instead of the default. */
    introParagraph?: string;
    /** When set, shown next to the "Why we created this guide" heading. */
    headerIcon?: string;
    /** When "standard", bullets use a disc instead of point icons. */
    bulletStyle?: 'icon' | 'standard';
  };
  eventSectionDescription: {
    title: string;
    description: string;
  };
  featuresTarget: string;
  /** Optional. When set, the page can render FeaturedEventsSection with these props. */
  featuredSection?: FeaturedSectionConfig;
  /** Optional. Cards for "Related guides" with optional image per guide. */
  relatedGuides?: RelatedGuide[];
};

// Event data type definition
export type Events = {
  events: Event[];  // Array of all events
};

// Full page content for standalone pages (NYC + tokyo-local-food-experiences)
export const pagesContent: Record<string, PageContent> = {
  // Hero and guide copy for tokyo-local-food-experiences come from topics.ts (single source of truth).
  // Placeholder hero.description, guide.points, guide.closingText satisfy PageContent type; DiscoverTopicPage overwrites them from topics.ts.
  'tokyo-local-food-experiences': {
    meta: {
      title: 'Eat like a local in Tokyo – Authentic food experiences & hidden gems',
      description: "Discover authentic Tokyo food experiences. Join local food tours, izakaya crawls, and culinary adventures that take you beyond tourist spots.",
    },
    hero: {
      description: '',
      city: 'Tokyo',
      heroImage: '/host/images/discover-images/tokyo-local-food-experiences-hero.svg',
      overlayLogo: '/host/images/discover-images/visiting-japan-logo.svg',
    },
    statisticsAudience: 'food',
    highlightsImage: '/host/images/discover-images/highlights-japanese-crafts-nyc.webp',
    guide: {
      points: [],
      guideImage: "/host/images/discover-images/tokyo-local-food-experiences-body.svg",
      closingText: '',
      headerIcon: '/host/images/discover-images/icon_fox_head.svg',
      bulletStyle: 'standard',
    },
    eventSectionDescription: {
      title: 'Authentic Tokyo food experiences',
      description: `From izakaya crawls to local market tours, these events take you beyond the guidebooks. Discover hidden gems, meet fellow food lovers, and experience Tokyo's culinary culture like a local.`
    },
    featuresTarget: 'Tokyo food experiences',
    featuredSection: {
      title: "Top Tokyo food & drink experiences",
      subtitle: "Skip the tourist traps—book izakaya, ramen, sake, and seasonal tastings with local hosts.",
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: {
        countryCode: 'jp',
        type: 'physical',
        sort: 'datetime asc',
        tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650],
      },
    },
    relatedGuides: [
      { href: '/us/discover/tokyo-traditions-and-culture', label: 'Tokyo: Traditions and culture', image: '/host/images/discover-images/guide-japanese-crafts-nyc.webp' },
      { href: '/us/discover/tokyo-anime-and-otaku-culture', label: 'Tokyo: Anime and otaku culture', image: '/host/images/discover-images/guide-anime-events-nyc.webp' },
      { href: '/us/discover/osaka-local-food-experiences', label: 'Osaka: Local food experiences', image: '/host/images/discover-images/guide-japanese-festivals-nyc.webp' },
      { href: '/us/discover/kyoto-traditions-and-culture', label: 'Kyoto: Traditions and culture', image: '/host/images/discover-images/hero-japanese-festivals-nyc.webp' },
    ],
  },

  // Kanagawa is a standalone custom page — only meta is needed for post-build.
  'experience-kanagawa': {
    meta: {
      title: 'KANAGAWA Block Party — A Japanese Art & Culture Experience | Peatix',
      description: 'KANAGAWA Block Party: A Japanese Art & Culture Experience. Brought to you by Kanagawa Prefecture, Japan. Free admission. March 14–15, 2026 at Japan Village, Industry City, Brooklyn.',
      image: 'https://peatix.com/us/discover/images/kanagawa/ogp.webp',
    },
  } as PageContent,
};

// Layout overrides for topic pages (merged with topics.ts by DiscoverTopicPage).
// These entries only carry featuredSection config; hero/guide copy comes from topics.ts.
export const topicOverrides: Record<string, Partial<PageContent>> = {
  'tokyo-traditions-and-culture': {
    featuredSection: {
      title: 'Upcoming cultural experiences in Tokyo',
      subtitle: 'Explore workshops, performances, and local traditions you can book in advance.',
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: { countryCode: 'jp', type: 'physical', sort: 'datetime asc', tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650] },
    },
  },
  'tokyo-food-and-drink': {
    featuredSection: {
      title: 'Upcoming food & drink experiences in Tokyo',
      subtitle: 'From tastings and tours to hands-on classes you can plan for and book in advance.',
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: { countryCode: 'jp', type: 'physical', sort: 'datetime asc', tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650] },
    },
  },
  'tokyo-anime-and-otaku-culture': {
    featuredSection: {
      title: 'Upcoming anime & otaku events in Tokyo',
      subtitle: 'Find screenings, fan meetups, and themed experiences you can add to your itinerary.',
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: { countryCode: 'jp', type: 'physical', sort: 'datetime asc', tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650] },
    },
  },
  'tokyo-nightlife-and-bar-scene': {
    featuredSection: {
      title: 'Upcoming nightlife events in Tokyo',
      subtitle: 'Bar events, tastings, and social meetups you can plan for and book ahead.',
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: { countryCode: 'jp', type: 'physical', sort: 'datetime asc', tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650] },
    },
  },
  'tokyo-sake-and-brewery-tours': {
    featuredSection: {
      title: 'Upcoming sake & brewery experiences in Tokyo',
      subtitle: 'Tastings, community events, and drink-focused meetups you can book in advance.',
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: { countryCode: 'jp', type: 'physical', sort: 'datetime asc', tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650] },
    },
  },
  'tokyo-seasonal-festivals-and-events': {
    featuredSection: {
      title: 'Upcoming seasonal festivals in Tokyo',
      subtitle: 'Find experiences that match your travel dates and book ticketed events in advance.',
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: { countryCode: 'jp', type: 'physical', sort: 'datetime asc', tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650] },
    },
  },
  'kyoto-local-food-experiences': {
    featuredSection: {
      title: 'Upcoming Kyoto food experiences',
      subtitle: 'Seasonal tastings, small events, and community gatherings you can book in advance.',
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: { countryCode: 'jp', type: 'physical', sort: 'datetime asc', tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650] },
    },
  },
  'kyoto-traditions-and-culture': {
    featuredSection: {
      title: 'Upcoming cultural experiences in Kyoto',
      subtitle: "Events inspired by the city's history and seasonal traditions.",
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: { countryCode: 'jp', type: 'physical', sort: 'datetime asc', tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650] },
    },
  },
  'kyoto-culture-and-experiences': {
    featuredSection: {
      title: 'Upcoming cultural experiences in Kyoto',
      subtitle: 'Workshops, events, and community gatherings that fit easily into your trip.',
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: { countryCode: 'jp', type: 'physical', sort: 'datetime asc', tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650] },
    },
  },
  'osaka-local-food-experiences': {
    featuredSection: {
      title: 'Upcoming Osaka food experiences',
      subtitle: 'Tastings, community events, and hands-on activities you can book ahead of your trip.',
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: { countryCode: 'jp', type: 'physical', sort: 'datetime asc', tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650] },
    },
  },
  'osaka-food-and-drink': {
    featuredSection: {
      title: 'Upcoming food & drink experiences in Osaka',
      subtitle: 'From casual tastings to local events you can plan for and book in advance.',
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: { countryCode: 'jp', type: 'physical', sort: 'datetime asc', tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650] },
    },
  },
  'osaka-traditions-and-culture': {
    featuredSection: {
      title: 'Upcoming cultural experiences in Osaka',
      subtitle: 'Workshops, meetups, and community experiences you can plan for.',
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: { countryCode: 'jp', type: 'physical', sort: 'datetime asc', tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650] },
    },
  },
  'japan-top-experiences': {
    featuredSection: {
      title: 'Must-do experiences across Japan',
      subtitle: 'Standout events and activities you can plan for, book, and build into your itinerary.',
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: { countryCode: 'jp', type: 'physical', sort: 'datetime asc', tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650] },
    },
  },
  'japan-food-and-drink-experiences': {
    featuredSection: {
      title: 'Upcoming food experiences across Japan',
      subtitle: 'From tastings and tours to hands-on workshops you can book in advance.',
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: { countryCode: 'jp', type: 'physical', sort: 'datetime asc', tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650] },
    },
  },
  'japan-culture-and-experiences': {
    featuredSection: {
      title: 'Upcoming cultural experiences in Japan',
      subtitle: 'Events, workshops, and local gatherings you can add to your itinerary.',
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: { countryCode: 'jp', type: 'physical', sort: 'datetime asc', tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650] },
    },
  },
  'japan-anime-and-otaku': {
    featuredSection: {
      title: 'Upcoming anime & otaku events in Japan',
      subtitle: 'Fan events, meetups, and themed activities you can plan for and book ahead.',
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: { countryCode: 'jp', type: 'physical', sort: 'datetime asc', tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650] },
    },
  },
  'japan-festivals-and-celebrations': {
    featuredSection: {
      title: 'Upcoming festivals and events in Japan',
      subtitle: 'Seasonal celebrations and ticketed experiences you can plan for and book in advance.',
      fetchMode: 'search',
      itemsPerPage: 6,
      searchQuery: { countryCode: 'jp', type: 'physical', sort: 'datetime asc', tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650] },
    },
  },
};

// Event item props type definition
export type EventItemProps = {
  event: Event;  // Single event object used in EventItem component
  isFourCols?: boolean; // When true, render compact card padding for 4-column layout
}; 